import type { EmbeddedLyrics } from '../metadata.ts';

export const PRESENTATION_DB = 'simple-music.local-presentation.v1';
const store = 'tracks';
export type LocalPresentation = { id: string; version: number; fingerprint: string; size?: number; artwork?: Blob; embeddedCover: boolean; lyrics: EmbeddedLyrics; lyricOverride?: boolean; id3v22LyricsChecked?: boolean; status?: 'ready' | 'failed'; updatedAt: string };
let database: Promise<IDBDatabase> | null = null;
const open = () => database ||= new Promise<IDBDatabase>((resolve, reject) => {
  const request = indexedDB.open(PRESENTATION_DB, 1);
  request.onupgradeneeded = () => request.result.createObjectStore(store, { keyPath: 'id' });
  request.onsuccess = () => { const db = request.result; db.onversionchange = () => { db.close(); database = null; }; resolve(db); };
  request.onerror = () => { database = null; reject(request.error); };
});
export async function readLocalPresentations(ids: Set<string>) {
  const db = await open();
  const entries = await new Promise<LocalPresentation[]>((resolve, reject) => { const request = db.transaction(store, 'readonly').objectStore(store).getAll(); request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error); });
  // Covers/lyrics are a cache only. Prune removed indexes without touching the
  // original audio DB, disk files or credentials.
  const removed = entries.filter(entry => !ids.has(entry.id));
  if (removed.length) { const transaction = db.transaction(store, 'readwrite'); for (const entry of removed) transaction.objectStore(store).delete(entry.id); }
  return new Map(entries.filter(entry => ids.has(entry.id)).map(entry => [entry.id, entry]));
}
export async function storeLocalPresentation(value: LocalPresentation) {
  const db = await open();
  await new Promise<void>((resolve, reject) => { const transaction = db.transaction(store, 'readwrite'); transaction.objectStore(store).put(value); transaction.oncomplete = () => resolve(); transaction.onerror = () => reject(transaction.error); transaction.onabort = () => reject(transaction.error); });
}
export async function thumbnailArtwork(picture: { data: Uint8Array; format: string }) {
  const original = new Blob([picture.data as BlobPart], { type: picture.format || 'image/jpeg' });
  if (typeof createImageBitmap === 'undefined' || typeof OffscreenCanvas === 'undefined') return original;
  let bitmap: ImageBitmap | undefined;
  try {
    bitmap = await createImageBitmap(original); const scale = Math.min(1, 320 / Math.max(bitmap.width, bitmap.height));
    const canvas = new OffscreenCanvas(Math.max(1, Math.round(bitmap.width * scale)), Math.max(1, Math.round(bitmap.height * scale)));
    const context = canvas.getContext('2d'); if (!context) return original;
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return await canvas.convertToBlob({ type: 'image/webp', quality: .86 });
  } catch { return original; }
  finally { bitmap?.close(); }
}
